import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import CloseIcon from 'components/icons/close';
import { useFormat } from 'helpers/hooks/useFormat';
import { Discount } from 'types/entity/cart';

export interface Props {
  className?: string;
  discounts: Discount[];
  onApplyDiscountCode?: (code: string) => Promise<void>;
  onRemoveDiscountCode?: (discount: Discount) => Promise<void>;
}

const DiscountForm: React.FC<Props> = ({ className, discounts, onApplyDiscountCode, onRemoveDiscountCode }) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });

  const [code, setCode] = useState('');
  const [codeIsInvalid, setCodeIsInvalid] = useState(false);
  const [erroMessage, setErrorMessage] = useState('');

  const [processing, setProcessing] = useState(false);

  const inputClassName = classNames(
    'h-10 w-full border rounded-sm px-[10px] py-3 text-[14px] placeholder:text-secondary-black disabled:bg-neutral-300 focus:outline-none',
    {
      'border-accent-red text-accent-red focus:border-accent-red': codeIsInvalid,
      'border-neutral-300': !codeIsInvalid,
    },
  );

  const discountsContainerClassName = classNames('mt-2 flex flex-wrap justify-items-start gap-3', {
    'pt-0': discounts?.length === 0,
    'pt-1': discounts?.length > 0,
  });

  const containerClassName = classNames('py-4 text-[16px] border-t border-neutral-400 text-[14px]', className);

  const onApplyDiscount = () => {
    if (processing || !code) return;

    setProcessing(true);

    onApplyDiscountCode?.(code)
      .then(() => setCode(''))
      .catch((err: Error) => {
        if (err.message.includes('MaxApplicationReached')) {
          setErrorMessage(
            formatMessage({
              id: 'error.maxUsageReached',
              defaultMessage:
                'Questo codice sconto non può più essere utilizzato poiché è stato raggiunto il numero massimo di applicazioni. {codes}',
              values: { codes: '' },
            }),
          );
        } else {
          setErrorMessage(formatMessage({ id: 'error.codeNotValid', defaultMessage: 'Codice sconto non valido.' }));
        }

        setCodeIsInvalid(true);
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
    setCodeIsInvalid(false);
  };

  const handleRemove = (discount: Discount) => {
    onRemoveDiscountCode?.(discount);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onApplyDiscount();
  };

  const resetCode = () => {
    setCode('');
    setCodeIsInvalid(false);
  };

  return (
    <div className={containerClassName}>
      <p>{formatMessage({ id: 'discounts.applyDiscount', defaultMessage: 'Applica codice sconto' })}</p>
      <div>
        <form className="mt-6" onSubmit={handleSubmit}>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                className={inputClassName}
                value={code}
                placeholder={formatMessage({
                  id: 'discounts.insertCode',
                  defaultMessage: 'Inserisci il codice',
                })}
                onChange={handleChange}
                disabled={processing}
              />
              {codeIsInvalid && (
                <span
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                  onClick={(e) => {
                    e.stopPropagation();
                    resetCode();
                  }}
                >
                  <CloseIcon className="size-[10px] cursor-pointer" />
                </span>
              )}
            </div>
            <button
              type="submit"
              disabled={processing || !code}
              className="h-10 cursor-pointer rounded-sm bg-primary-black px-4 py-2 text-[14px] font-medium text-white transition-colors hover:bg-secondary-black disabled:cursor-not-allowed disabled:bg-neutral-400"
            >
              {formatMessage({ id: 'discounts.apply', defaultMessage: 'Applica' })}
            </button>
          </div>
          {codeIsInvalid && (
            <p className="mt-4 font-body text-[12px] font-medium leading-normal text-accent-red">{erroMessage}</p>
          )}
        </form>

        {discounts && !!discounts.length && (
          <div className={discountsContainerClassName}>
            {discounts.map((discount) => (
              <div
                key={discount.discountId}
                className="mr-2 flex w-fit justify-between gap-2 rounded-sm border border-neutral-400 bg-white px-2 py-1"
              >
                <label className="text-[12px] uppercase leading-[16px] text-secondary-black">{discount.code}</label>
                <button type="button" onClick={() => handleRemove(discount)}>
                  <XMarkIcon className="size-4 text-secondary-black" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscountForm;
